// =============================================================================
// MultiSiteBlock — comparativa entre centros + top usuarios
//
// v1.2: el bloque solo se renderiza cuando allSites=true (opción A).
// El toggle "Ver todos los centros" se ha movido a la cabecera del
// DashboardPage para ser visible aunque el bloque esté oculto. Aquí
// eliminamos la cabecera con checkbox y el aviso informativo.
// =============================================================================

import type { MultiSite } from '../../types/dashboard';
import { SiteBarsChart } from './charts/SiteBarsChart';

interface MultiSiteBlockProps {
  data: MultiSite | null;
  loading: boolean;
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

export function MultiSiteBlock({ data, loading }: MultiSiteBlockProps) {
  return (
    <section className="space-y-4">
      <header className="flex items-center gap-2">
        <span className="material-symbols-outlined text-primary">apartment</span>
        <h2 className="text-base font-semibold text-slate-900">
          Comparativa entre centros
        </h2>
      </header>

      {/* Comparativa por centro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {loading ? (
            <div className="h-40 bg-slate-50 rounded animate-pulse" />
          ) : (
            <SiteBarsChart
              data={data?.sites ?? []}
              metric="revenue"
              color="#22c55e"
              formatValue={formatMoney}
            />
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {loading ? (
            <div className="h-40 bg-slate-50 rounded animate-pulse" />
          ) : (
            <SiteBarsChart
              data={data?.sites ?? []}
              metric="acceptanceRate"
              color="#008DD0"
              formatValue={(v) => `${v.toFixed(1)}%`}
            />
          )}
        </div>
      </div>

      {/* Top 3 usuarios */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">
          Top usuarios por actividad
        </h3>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-slate-50 rounded animate-pulse" />
            ))}
          </div>
        ) : !data || data.topUsers.length === 0 ? (
          <p className="text-sm text-slate-400 py-3">
            Sin actividad de usuarios en este periodo.
          </p>
        ) : (
          <ul className="space-y-1">
            {data.topUsers.map((u, idx) => (
              <li
                key={u.userId}
                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-slate-50"
              >
                <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <span className="flex-1 font-medium text-slate-700">
                  {u.userName || 'Usuario'}
                </span>
                <span className="text-xs text-slate-500">
                  {u.budgetsCreated} presupuestos
                </span>
                <span className="text-sm font-semibold text-slate-900 min-w-[80px] text-right">
                  {formatMoney(u.revenue)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
